import { faBug, faCode, faWindowRestore } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Modal, ModalBody, ModalHeader, ModalFooter, Textarea, Button, Input, ModalContent, ButtonGroup } from '@heroui/react';
import { useEffect, useRef, useState } from 'react';
import FileUpload from '../../../shared/components/file-upload/FileUpload';
import { useForm } from 'react-hook-form';
import { EFeedbackType, TContactUsFormInputs } from '../contactUsTypes';
import React from 'react';
import ModalManager from '../../../shared/components/modal/ModalManager';
import SharedSpinner from '../../../shared/components/spinner/SharedSpinner';
import { authApiV1 } from '../../../axiosWrapper';
import { 
    trackEvent,
    trackExceptionWithContext
} from '../../../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../../services/application-insights/appInsightsConstants';

interface IContactUsModalProps {
	isOpen: boolean;
	onOpenChange: () => void;
	onClose: () => void;
}

const ContactUsModal: React.FC<IContactUsModalProps> = ({ isOpen, onOpenChange, onClose }) => {
	const [feedbackType, setFeedbackType] = useState('Feature');
	const [isBusy, setIsBusy] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors, isValid },
		setValue,
		reset,
	} = useForm<TContactUsFormInputs>({
		defaultValues: {
			feedbackType: EFeedbackType.Feature,
			subject: '',
			message: '',
			files: null,
		},
	});

	const fileUploadRef = useRef<{ clearFiles: () => void } | null>(null);

	// Register the file input in the parent component
	useEffect(() => {
		register('files');
	}, [register]);

	// Track modal opening
	useEffect(() => {
		if (isOpen) {
			trackEvent(
				AppInsightsActions.COMPONENT_MOUNT,
				{
					component: AppInsightsComponents.CONTACT_US_MODAL,
					modalState: 'opened'
				}
			);
		}
	}, [isOpen]);

	const onSubmit = async (data: TContactUsFormInputs) => {
		const submissionId = `contact-us-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
		setIsBusy(true);

		trackEvent(
			AppInsightsActions.FORM_SUBMIT_ATTEMPT,
			{
				submissionId,
				component: AppInsightsComponents.CONTACT_US_MODAL,
				feedbackType: data.feedbackType,
				hasSubject: !!data.subject,
				hasMessage: !!data.message,
				hasFiles: !!(data.files && data.files.length > 0),
				filesCount: data.files?.length || 0
			}
		);

		const formData = new FormData();
		formData.append('feedbackType', data.feedbackType);
		formData.append('subject', data.subject);
		formData.append('message', data.message);

		if (data.files) {
			Array.from(data.files).forEach((file) => formData.append('files', file));
		}

		try {
			const response = await authApiV1.post('/account/SendFeedbackV2', formData, {
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			});
			if (response.status === 200 && response.data.success) {
				trackEvent(
					AppInsightsActions.FORM_SUBMIT_SUCCESS,
					{
						submissionId,
						component: AppInsightsComponents.CONTACT_US_MODAL,
						feedbackType: data.feedbackType,
						responseStatus: response.status
					}
				);

				reset();
				fileUploadRef.current?.clearFiles();
				setIsBusy(false);
				ModalManager.show({
					header: 'Success',
					message: 'Your feedback has been sent successfully!',
					showActionButton: false,
					cancelButtonText: 'Close',
					onCancel: () => {
						handleOnClose();
					},
					size: 'xs',
				});

				return;
			} else {
				throw new Error('Failed to send feedback. Please try again.');
			}
		} catch (error: any) {
			trackExceptionWithContext(
				error instanceof Error ? error : new Error(String(error)),
				{
					component: AppInsightsComponents.CONTACT_US_MODAL,
					action: AppInsightsActions.FORM_SUBMIT_ATTEMPT,
					additionalData: {
						submissionId,
						errorType: AppInsightsErrorTypes.FORM_SUBMISSION_ERROR,
						feedbackType: data.feedbackType,
						responseStatus: error.response?.status || 'no-response'
					}
				}
			);

			setIsBusy(false);
			ModalManager.show({
				header: error.name,
				message: 'Failed to send feedback. Please try again. \nError: ' + error.message,
				messageDetails: error.stack,
				size: 'sm',
				showActionButton: false,
				cancelButtonText: 'Close',
			});
		}
	};

	const handleFileChange = (files: FileList | null) => {
		setValue('files', files);
	};

	const handleFeedbackTyopeOnClick = (value: string) => {
		setFeedbackType(value);
		setValue('feedbackType', value);
	};

	const handleOnClose = () => {
		onClose();
		fileUploadRef.current?.clearFiles();
		reset();
	};

	return (
		<Modal
			className="rounded"
			classNames={{
				closeButton: 'rounded',
			}}
			size="md"
			isOpen={isOpen}
			onClose={handleOnClose}
			onOpenChange={onOpenChange}
		>
			<ModalContent>
				<form>
					<ModalHeader className="flex flex-col gap-1">Contact Us</ModalHeader>
					<ModalBody>
						<ButtonGroup
							size="sm"
							className="flex"
							radius="none"
							{...register('feedbackType', { required: 'Feedback type is required' })}
						>
							<Button
								color="primary"
								className={`flex-1 opacity-${feedbackType === EFeedbackType.Error ? '100' : '50'}`}
								onPress={() => handleFeedbackTyopeOnClick(EFeedbackType.Error)}
							>
								<FontAwesomeIcon icon={faBug} /> Error
							</Button>
							<Button
								color="primary"
								className={`flex-1 opacity-${feedbackType === EFeedbackType.Feature ? '100' : '50'}`}
								onPress={() => handleFeedbackTyopeOnClick(EFeedbackType.Feature)}
							>
								<FontAwesomeIcon icon={faWindowRestore} /> Feature
							</Button>
							<Button
								color="primary"
								className={`flex-1 opacity-${feedbackType === EFeedbackType.Improvement ? '100' : '50'}`}
								onPress={() => handleFeedbackTyopeOnClick(EFeedbackType.Improvement)}
							>
								<FontAwesomeIcon icon={faCode} /> Improvement
							</Button>
						</ButtonGroup>
						<Input
							size="md"
							fullWidth
							classNames={{
								inputWrapper: 'rounded',
							}}
							label="Subject"
							isInvalid={errors.subject ? true : false}
							errorMessage={errors.subject && <p>{errors.subject.message}</p>}
							isRequired
							placeholder="Enter subject"
							{...register('subject', { required: 'Subject is required' })}
						/>
						<Textarea
							size="sm"
							fullWidth
							classNames={{
								inputWrapper: 'rounded',
							}}
							minRows={12}
							label="Message"
							isInvalid={errors.message ? true : false}
							errorMessage={errors.message && <p>{errors.message.message}</p>}
							isRequired
							placeholder="Enter your message"
							{...register('message', { required: 'Message is required' })}
						/>
						<FileUpload
							ref={fileUploadRef}
							acceptedExtensions={['.png', '.jpg', '.jpeg']}
							multiple={true}
							maxFiles={3}
							onFilesSelected={handleFileChange}
						/>
					</ModalBody>
					<ModalFooter>
						<Button size="sm" color="danger" className="rounded" onPress={handleOnClose}>
							Close
						</Button>
						<Button
							isDisabled={!isValid}
							size="sm"
							type="button"
							color="primary"
							className="rounded"
							onPress={() => {
								handleSubmit(onSubmit)();
							}}
						>
							Send
						</Button>
					</ModalFooter>
				</form>
				<SharedSpinner isBusy={isBusy} size="sm" label="Sending..." />
			</ModalContent>
		</Modal>
	);
};

export default ContactUsModal;
